import React, { useState, useEffect, useCallback } from 'react';
import { getFieldOptions, addFieldOption, updateFieldOption, deleteFieldOption } from '../services/api';
import { useLang } from '../i18n/LanguageContext';

const ALL_FIELDS = [
  'silhouette', 'fabric', 'color', 'neckline', 'sleeve', 'sleeveStyle', 'bodice',
  'waist', 'length', 'skirtDetail', 'slitType', 'embroideryType', 'embroideryPosition',
];

export default function FieldOptionsPage() {
  const { lang, t } = useLang();
  const [allOptions, setAllOptions] = useState({});
  const [selectedField, setSelectedField] = useState('silhouette');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  const [newValue, setNewValue] = useState('');
  const [newLabelEn, setNewLabelEn] = useState('');
  const [newLabelAr, setNewLabelAr] = useState('');
  const [newGroupEn, setNewGroupEn] = useState('');
  const [newGroupAr, setNewGroupAr] = useState('');
  const [editingId, setEditingId] = useState(null);

  const isEmbroideryPos = selectedField === 'embroideryPosition';

  const loadOptions = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getFieldOptions();
      setAllOptions(data);
    } catch {
      setError(t.fieldOptionsLoadFailed);
    }
    setLoading(false);
  }, [t]);

  useEffect(() => { loadOptions(); }, [loadOptions]);

  const currentOptions = allOptions[selectedField] || [];

  const clearForm = () => {
    setNewValue(''); setNewLabelEn(''); setNewLabelAr('');
    setNewGroupEn(''); setNewGroupAr(''); setEditingId(null);
  };

  const showSuccess = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(''), 2500);
  };

  const handleEdit = (opt) => {
    setEditingId(opt.id);
    setNewValue(opt.value || '');
    setNewLabelEn(opt.labelEn || '');
    setNewLabelAr(opt.labelAr || '');
    setNewGroupEn(opt.groupNameEn || '');
    setNewGroupAr(opt.groupNameAr || '');
    setError(''); setSuccessMsg('');
    // Scroll to form
    setTimeout(() => document.getElementById('field-option-form')?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 100);
  };

  const handleAdd = async () => {
    if (!newValue.trim() || !newLabelEn.trim() || !newLabelAr.trim()) return;
    setSaving(true); setError(''); setSuccessMsg('');
    try {
      await addFieldOption({
        fieldName: selectedField,
        value: newValue.trim(),
        labelEn: newLabelEn.trim(),
        labelAr: newLabelAr.trim(),
        groupNameEn: isEmbroideryPos ? newGroupEn.trim() || null : null,
        groupNameAr: isEmbroideryPos ? newGroupAr.trim() || null : null,
        sortOrder: 0,
      });
      clearForm();
      await loadOptions();
      showSuccess('Added successfully');
    } catch {
      setError('Failed to add option.');
    }
    setSaving(false);
  };

  const handleUpdate = async () => {
    if (!newValue.trim() || !newLabelEn.trim() || !newLabelAr.trim()) return;
    setSaving(true); setError(''); setSuccessMsg('');
    try {
      await updateFieldOption(editingId, {
        fieldName: selectedField,
        value: newValue.trim(),
        labelEn: newLabelEn.trim(),
        labelAr: newLabelAr.trim(),
        groupNameEn: isEmbroideryPos ? newGroupEn.trim() || null : null,
        groupNameAr: isEmbroideryPos ? newGroupAr.trim() || null : null,
        sortOrder: 0,
      });
      clearForm();
      await loadOptions();
      showSuccess('Updated successfully');
    } catch {
      setError('Failed to update option.');
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t.fieldDeleteConfirm)) return;
    try {
      await deleteFieldOption(id);
      await loadOptions();
    } catch {
      setError('Failed to delete.');
    }
  };

  const inputClasses = "w-full px-4 py-2.5 border border-border rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-[#FA6868] focus:border-[#FA6868] transition-shadow hover:border-gray-300 shadow-sm bg-white text-gray-800";
  const labelClasses = "flex items-center gap-1.5 text-[11px] font-semibold text-[#64748b] uppercase tracking-wide";
  const iconClasses = "material-symbols-outlined text-[16px] text-[#f59e0b]";

  return (
    <div className="max-w-[1100px] w-full mx-auto px-6 py-8 pb-12 space-y-8">
      
      {/* Field Selector */}
      <div className="bg-surface rounded-xl p-8 shadow-soft border border-border mt-4">
        <h3 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-3">
          <span className="w-[3px] h-6 bg-[#fbbf24] rounded-full"></span>
          {t.fieldSelectLabel}
        </h3>
        <div className="space-y-2">
          <label className={labelClasses}>
            <span className={iconClasses}>category</span>
            Field Name
          </label>
          <select
            value={selectedField}
            onChange={(e) => { setSelectedField(e.target.value); setError(''); setSuccessMsg(''); clearForm(); }}
            className={`custom-select ${inputClasses} cursor-pointer`}
          >
            {ALL_FIELDS.map((f) => (
              <option key={f} value={f}>{t.fieldNames[f]}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Current Options List */}
      <div className="bg-surface rounded-xl p-8 shadow-soft border border-border">
        <h3 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-3">
          <span className="w-[3px] h-6 bg-[#fbbf24] rounded-full"></span>
          {t.fieldNames[selectedField]} 
          <span className="text-xs font-semibold text-[#64748b] bg-gray-100 px-2.5 py-1 rounded-md ml-2">
            {currentOptions.length} {lang === 'ar' ? 'خيار' : 'option(s)'}
          </span>
        </h3>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-8">
            <div className="w-10 h-10 border-4 border-gray-200 border-t-[#FA6868] rounded-full animate-spin" />
            <p className="text-[#64748b] mt-4 text-sm font-medium">{t.fieldOptionsLoading}</p>
          </div>
        ) : currentOptions.length === 0 ? (
          <div className="text-center py-10 bg-gray-50 rounded-xl border border-dashed border-border">
            <span className="material-symbols-outlined text-4xl text-gray-300 block mb-3">inbox</span>
            <p className="text-sm font-medium text-[#64748b]">{t.fieldOptionsEmpty}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {currentOptions.map((opt, idx) => (
              <div key={opt.id} className={`flex items-center justify-between px-5 py-3.5 rounded-xl bg-gray-50 border group hover:border-[#FA6868]/40 hover:bg-red-50/30 transition-colors shadow-sm cursor-pointer ${editingId === opt.id ? 'border-[#FA6868] bg-red-50/40 ring-1 ring-[#FA6868]/30' : 'border-gray-200'}`}
                onClick={() => handleEdit(opt)}
              >
                <div className="flex-1 min-w-0 flex items-center flex-wrap gap-2">
                  <span className="text-xs font-bold text-[#64748b] bg-white border border-gray-200 px-2 py-0.5 rounded mr-1">#{idx + 1}</span>
                  <span className="text-sm font-semibold text-gray-800">
                    {lang === 'ar' ? opt.labelAr : opt.labelEn}
                  </span>
                  <span className="text-[#64748b] mx-1">→</span>
                  <span className="text-xs text-[#64748b] font-mono bg-gray-200/50 px-2 py-0.5 rounded">{opt.value}</span>
                  {opt.groupNameEn && (
                    <span className="text-[11px] font-bold text-[#fbbf24] uppercase tracking-wide ml-2 bg-amber-50 px-2 py-0.5 rounded border border-amber-100">
                      {lang === 'ar' ? opt.groupNameAr : opt.groupNameEn}
                    </span>
                  )}
                  {editingId === opt.id && (
                    <span className="text-[11px] font-bold text-[#FA6868] bg-red-50 px-2 py-0.5 rounded border border-red-200 ml-1">
                      {lang === 'ar' ? 'تعديل' : 'Editing'}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={(e) => { e.stopPropagation(); handleEdit(opt); }}
                    className="text-[#64748b] hover:text-[#FA6868] text-sm p-1.5 rounded-md hover:bg-orange-50 transition-colors cursor-pointer"
                    title="Edit"
                  >
                    <span className="material-symbols-outlined text-[18px]">edit</span>
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDelete(opt.id); }}
                    className="text-[#64748b] hover:text-[#FA6868] text-sm p-1.5 rounded-md hover:bg-red-50 transition-colors cursor-pointer"
                    title="Delete"
                  >
                    <span className="material-symbols-outlined text-[18px]">delete</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add New Option */}
      <div id="field-option-form" className="bg-surface rounded-xl p-8 shadow-soft border border-border">
        <h3 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-3">
          <span className="w-[3px] h-6 bg-[#fbbf24] rounded-full"></span>
          {editingId ? (lang === 'ar' ? 'تعديل الخيار' : 'Edit Option') : t.fieldAddBtn}
          {editingId && (
            <span className="text-xs font-semibold text-[#FA6868] bg-red-50 px-2.5 py-1 rounded-md ml-2 border border-red-200">
              ID: {editingId}
            </span>
          )}
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-6">
          <div className="space-y-2">
            <label className={labelClasses}>
              <span className={iconClasses}>code</span>
              {t.fieldAddValue}
            </label>
            <input
              value={newValue} onChange={(e) => setNewValue(e.target.value)}
              className={inputClasses}
              placeholder="e.g. Ball Gown"
            />
          </div>
          <div className="space-y-2">
            <label className={labelClasses}>
              <span className={iconClasses}>translate</span>
              {t.fieldAddLabelEn}
            </label>
            <input
              value={newLabelEn} onChange={(e) => setNewLabelEn(e.target.value)}
              className={inputClasses}
              placeholder="Ball Gown"
            />
          </div>
          <div className="space-y-2">
            <label className={labelClasses}>
              <span className={iconClasses}>translate</span>
              {t.fieldAddLabelAr}
            </label>
            <input
              value={newLabelAr} onChange={(e) => setNewLabelAr(e.target.value)}
              className={inputClasses}
              dir="rtl"
              placeholder="فستان سهرة"
            />
          </div>
        </div>

        {isEmbroideryPos && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
            <div className="space-y-2">
              <label className={labelClasses}>
                <span className={iconClasses}>workspaces</span>
                {t.fieldAddGroupEn}
              </label>
              <input
                value={newGroupEn} onChange={(e) => setNewGroupEn(e.target.value)}
                className={inputClasses}
                placeholder="e.g. Neckline Area"
              />
            </div>
            <div className="space-y-2">
              <label className={labelClasses}>
                <span className={iconClasses}>workspaces</span>
                {t.fieldAddGroupAr}
              </label>
              <input
                value={newGroupAr} onChange={(e) => setNewGroupAr(e.target.value)}
                className={inputClasses}
                dir="rtl"
                placeholder="منطقة العنق"
              />
            </div>
          </div>
        )}

        <div className="flex items-center justify-end gap-3 mt-8 pt-6 border-t border-gray-100">
          {editingId && (
            <button
              onClick={clearForm}
              className="bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-2.5 px-6 rounded-lg transition-all duration-200 flex items-center gap-2 text-sm cursor-pointer"
            >
              <span className="material-symbols-outlined text-[18px]">close</span>
              {lang === 'ar' ? 'إلغاء' : 'Cancel'}
            </button>
          )}
          <button
            onClick={editingId ? handleUpdate : handleAdd}
            disabled={saving || !newValue.trim() || !newLabelEn.trim() || !newLabelAr.trim()}
            className={`${editingId ? 'bg-[#f59e0b] hover:bg-[#d97706]' : 'bg-[#FA6868] hover:bg-[#e65b5b]'} text-white font-semibold py-2.5 px-8 rounded-lg shadow-sm transition-all duration-200 flex items-center gap-2 text-sm ${
              saving || !newValue.trim() || !newLabelEn.trim() || !newLabelAr.trim()
                ? 'opacity-50 cursor-not-allowed'
                : 'cursor-pointer hover:shadow-md'
            }`}
          >
            <span className="material-symbols-outlined text-[18px]">{editingId ? 'save' : 'add'}</span>
            {saving ? t.fieldSaving : editingId ? (lang === 'ar' ? 'تحديث' : 'Update') : t.fieldAddBtn}
          </button>
        </div>

        {successMsg && (
          <div className="mt-4 px-4 py-3 bg-emerald-50 text-emerald-600 rounded-lg text-sm font-semibold border border-emerald-100 flex items-center gap-2">
            <span className="material-symbols-outlined text-[18px]">check_circle</span>
            {successMsg}
          </div>
        )}
        {error && (
          <div className="mt-4 px-4 py-3 bg-red-50 text-[#FA6868] rounded-lg text-sm font-semibold border border-red-100 flex items-center gap-2">
            <span className="material-symbols-outlined text-[18px]">error</span>
            {error}
          </div>
        )}
      </div>
    </div>
  );
}